// ═══════════════════════════════════════════════════════════════
// PersonNode.js — Noeud custom React Flow (Family P2c)
// ═══════════════════════════════════════════════════════════════
// Affiche une personne : avatar initiales, full_name, badges
// (admin2, nationalités, mineur). Couleur liseré = cohabitation
// primaire (ou neutre si aucune).
// ═══════════════════════════════════════════════════════════════

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { Shield, User } from 'lucide-react';

const initials = (name) => {
    if (!name) return '?';
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0]?.toUpperCase() || '')
        .join('');
};

const PersonNode = ({ data, selected }) => {
    const { person, cohabitationColor, isDark } = data;
    const surface = isDark ? '#1a1a2e' : '#ffffff';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = cohabitationColor || (isDark ? '#2a2a44' : '#e2e8f0');

    const isMinor = (() => {
        if (!person.birth_date) return false;
        const birth = new Date(person.birth_date);
        const age = (Date.now() - birth.getTime()) / (365.25 * 24 * 3600 * 1000);
        return age < 18;
    })();

    const accent = cohabitationColor || '#1D4ED8';

    return (
        <div
            style={{
                background: isDark
                    ? `linear-gradient(145deg, #1a1a2e 0%, #16162a 100%)`
                    : `linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)`,
                color: textPrimary,
                border: `1px solid ${cohabitationColor ? `${cohabitationColor}55` : (isDark ? '#2a2a44' : '#e2e8f0')}`,
                borderRadius: 9,
                padding: '7px 10px',
                minWidth: 140,
                maxWidth: 162,
                boxShadow: selected
                    ? `0 0 0 2px ${accent}66, 0 6px 18px rgba(0,0,0,0.22)`
                    : isDark
                        ? '0 1px 3px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.02)'
                        : '0 1px 2px rgba(15,23,42,0.06), 0 1px 3px rgba(15,23,42,0.04)',
                transition: 'box-shadow 0.18s, border-color 0.18s, transform 0.12s',
                cursor: 'pointer',
            }}
        >
            <Handle type="target" position={Position.Top} id="t" style={{ opacity: 0, pointerEvents: 'none' }} />
            <Handle type="target" position={Position.Left} id="tl" style={{ opacity: 0, pointerEvents: 'none', top: '50%' }} />
            <Handle type="source" position={Position.Left} id="sl" style={{ opacity: 0, pointerEvents: 'none', top: '50%' }} />
            <Handle type="target" position={Position.Right} id="tr" style={{ opacity: 0, pointerEvents: 'none', top: '50%' }} />
            <Handle type="source" position={Position.Right} id="sr" style={{ opacity: 0, pointerEvents: 'none', top: '50%' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                    style={{
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        background: cohabitationColor
                            ? `linear-gradient(135deg, ${cohabitationColor}22 0%, ${cohabitationColor}33 100%)`
                            : (isDark ? 'linear-gradient(135deg, #2a2a44 0%, #1e1e38 100%)' : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)'),
                        color: accent,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 600,
                        fontSize: 10,
                        letterSpacing: 0.2,
                        flexShrink: 0,
                        boxShadow: `inset 0 0 0 1px ${accent}22`,
                    }}
                >
                    {initials(person.full_name)}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                        style={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            letterSpacing: -0.1,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: 1.25,
                        }}
                        title={person.full_name}
                    >
                        {person.full_name}
                    </div>
                    {person.role && (
                        <div
                            style={{
                                fontSize: 10,
                                color: textSecondary,
                                marginTop: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                fontWeight: 400,
                                letterSpacing: 0.1,
                            }}
                        >
                            {person.role}
                        </div>
                    )}
                </div>
            </div>

            {/* Badges */}
            {(person.is_admin2 || isMinor || (person.nationalities || []).length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 6 }}>
                    {person.is_admin2 && (
                        <Badge color="#a855f7" icon={<Shield size={8} strokeWidth={2.5} />} label="admin 2" />
                    )}
                    {isMinor && (
                        <Badge color="#f59e0b" icon={<User size={8} strokeWidth={2.5} />} label="mineur" />
                    )}
                    {(person.nationalities || []).slice(0, 3).map((nat) => (
                        <Badge key={nat} color="#10b981" label={nat} />
                    ))}
                    {(person.nationalities || []).length > 3 && (
                        <Badge color="#6b7280" label={`+${person.nationalities.length - 3}`} />
                    )}
                </div>
            )}

            <Handle type="source" position={Position.Bottom} id="s" style={{ opacity: 0, pointerEvents: 'none' }} />
        </div>
    );
};

const Badge = ({ color, icon, label }) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            padding: '1px 5px',
            fontSize: 9,
            fontWeight: 600,
            color,
            background: `${color}14`,
            border: `1px solid ${color}2a`,
            borderRadius: 4,
            lineHeight: 1.3,
            letterSpacing: 0.2,
            textTransform: 'uppercase',
        }}
    >
        {icon}
        {label}
    </span>
);

export default PersonNode;
